import React, { useEffect, useState } from 'react'
import {
  Background,
  Button,
  Modal,
  ModalClose,
  ModalContent,
} from '@mc/design-system'
import { Trans, useTranslation } from '@mc/i18n'
import { paths } from '@mc/paths'
import { trackExperimentViewed } from '@mc/track-event'
import FacebookButton from '../../../components/FacebookButton'
import GoogleButton from '../../../components/GoogleButton'
import { fbAuth, isSuccessful, redirect } from '../../../services'
import { trackPopUpVendorClicked } from '../events/socialVendorLogInInitiated'
import {
  trackLogInClicked,
  trackPopUpClosed,
  trackPopUpTappedOutside,
  trackPopUpViewed,
  trackSignUpWithEmailClicked,
} from '../events/socialVendorLogin'
import {
  setGoogleOneTapSignupClicked,
  setGoogleOneTapQuizSource,
} from '../hooks/googleOneTapQuizEntry'
import { GOOGLE_EMAIL_CAPTURE_NAMESPACE } from '../i18n'
import classes from './SocialPopUp.module.scss'

// https://app.growthbook.io/features/pen-3951_v2
export const GOOGLE_EMAIL_CAPTURE_FLAG = 'pen-3951_v2'

export const SocialPopUp = ({
  onDismiss,
  onClose,
  userAgent,
  nextPage = '/',
}: {
  onDismiss: () => void
  onClose: () => void
  userAgent: string
  nextPage?: string
}) => {
  const { t } = useTranslation(GOOGLE_EMAIL_CAPTURE_NAMESPACE)

  const [show, setShow] = useState(true)
  const [thirdPartyError, setThirdPartyError] = useState<string | undefined>()

  useEffect(() => {
    trackPopUpViewed()
    trackExperimentViewed(GOOGLE_EMAIL_CAPTURE_FLAG)
  }, [])

  const onFacebookLogin = () => {
    const onLogIn = () => {
      window.location.href = nextPage
      setGoogleOneTapQuizSource({ source: 'create_account_modal' })
    }

    fbAuth()
      .then((res) => {
        if (isSuccessful(res)) {
          redirect(res, onLogIn)
        }
      })
      .catch((err) => {
        setThirdPartyError(err.message)
      })
  }

  const onGoogleLogin = () => {
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}?next_page=${nextPage}`,
    )
    trackPopUpVendorClicked({ vendor: 'google' })
    setGoogleOneTapQuizSource({ source: 'create_account_modal' })
  }

  const onSignUpWithEmailClicked = () => {
    trackSignUpWithEmailClicked()
    setGoogleOneTapSignupClicked()
  }

  const onLoginClicked = () => {
    trackLogInClicked()
    setGoogleOneTapSignupClicked()
  }

  const handleClose = (source?: 'backdrop' | 'close' | string) => {
    setShow(false)

    if (source === 'close') {
      trackPopUpClosed()
      onClose()
    } else {
      trackPopUpTappedOutside()
      onDismiss()
    }
  }

  return (
    <Modal
      canClickOutsideToClose
      show={show}
      size='small'
      onClose={handleClose}
      className={classes.modal}
    >
      <ModalContent>
        <Background color='light' className='mc-invert mc-corners--4'>
          <ModalClose />

          <div className='mc-px-4 mc-py-9'>
            <h1 className='mc-text-h5 mc-text--center mc-mb-7'>{t('title')}</h1>

            <Button
              as='a'
              href={paths.signUp(nextPage)}
              fullWidth
              className='mc-mb-3'
              onClick={onSignUpWithEmailClicked}
            >
              {t('signUp.email')}
            </Button>

            <div className='mc-mb-3'>
              <p className='mc-text-x-small mc-text-hr mc-text--uppercase mc-text--bold'>
                <span>{t('or')}</span>
              </p>
            </div>

            <div className='mc-mb-4'>
              <GoogleButton userAgent={userAgent} onClick={onGoogleLogin}>
                {t('signUp.google')}
              </GoogleButton>
            </div>

            <div className='mc-mb-4'>
              <FacebookButton
                onLogin={onFacebookLogin}
                userAgent={userAgent}
                onClick={() => trackPopUpVendorClicked({ vendor: 'facebook' })}
              >
                {t('signUp.facebook')}
              </FacebookButton>
            </div>

            {thirdPartyError && (
              <p className='mc-text--error mc-text--center mc-mt-4'>
                {thirdPartyError}
              </p>
            )}

            <p className='mc-mt-9 mc-text-small mc-text--center mc-text--bold mc-text-color--tint'>
              <Trans
                i18nKey='signUp.existingAccount'
                ns={GOOGLE_EMAIL_CAPTURE_NAMESPACE}
                components={{
                  a: (
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    <a
                      href={paths.logIn(nextPage)}
                      className='mc-text--link'
                      style={{ color: 'var(--mc-color-neutral-800)' }}
                      onClick={onLoginClicked}
                    />
                  ),
                }}
              />
            </p>
          </div>
        </Background>
      </ModalContent>
    </Modal>
  )
}
